import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Wand2, ArrowLeft, MessageCircle, FileText, CreditCard, Users } from "lucide-react";

const Help = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </Button>
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-primary">Help Center</h1>
                <p className="text-sm text-muted-foreground">Get help with AI Humanizer Text</p>
              </div>
            </div>
            <Navigation />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">How can we help you?</h2>
          <p className="text-lg text-muted-foreground">
            Find answers to common questions and learn how to get the most out of AI Humanizer Text.
          </p>
        </div>

        {/* Quick Help Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card>
            <CardHeader className="text-center">
              <FileText className="w-8 h-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Getting Started</CardTitle>
              <CardDescription>Learn the basics</CardDescription>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="text-center">
              <CreditCard className="w-8 h-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Billing</CardTitle>
              <CardDescription>Plans & payments</CardDescription>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="text-center">
              <Users className="w-8 h-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Account</CardTitle>
              <CardDescription>Profile settings</CardDescription>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="text-center">
              <MessageCircle className="w-8 h-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Contact</CardTitle>
              <CardDescription>Get in touch</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>What is AI Humanizer Text?</AccordionTrigger>
              <AccordionContent>
                AI Humanizer Text is an advanced tool that transforms AI-generated content into natural, human-like writing. 
                It helps make robotic AI text sound more authentic and engaging for your readers.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>How do I use the text humanizer?</AccordionTrigger>
              <AccordionContent>
                Simply paste your AI-generated text into the input field, select your preferred humanization intensity, 
                and click "Humanize Text". The tool will process your content and provide a more natural version.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>What are credits and how do they work?</AccordionTrigger>
              <AccordionContent>
                Credits are used to process text humanization requests. Each humanization uses a certain number of credits 
                based on the text length and complexity. You can view your remaining credits in the top navigation bar.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>Can I humanize text without registering?</AccordionTrigger>
              <AccordionContent>
                You need to register for an account to use the humanization service. However, you can enter your text 
                before registering - it will be saved and processed automatically once you complete registration.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>What text lengths are supported?</AccordionTrigger>
              <AccordionContent>
                Our tool supports text of various lengths, from short paragraphs to longer articles. 
                Longer texts may use more credits and take slightly longer to process.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger>Is my text data secure?</AccordionTrigger>
              <AccordionContent>
                Yes, we take data security seriously. Your text is processed securely and is not stored permanently 
                on our servers. We use industry-standard encryption to protect your content.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Contact Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Need More Help?
            </CardTitle>
            <CardDescription>
              Can't find what you're looking for? Our support team is here to help.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <a href="mailto:support@aihumanizertext.com">
                  Contact Support
                </a>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/pricing">
                  View Pricing Plans
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Help;